import { faDollarSign, faHashtag } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  CheckboxDropdown,
  DatePickerWithRange,
  SearchableCheckboxDropdown,
} from '@repo/videa-components/src';
import { cn } from '@repo/videa-components/src/lib/utils';
import { useAtom } from 'jotai';

import type { TProviderRole } from '@/react-query/insights/types';
import { useGetPracticeGroups } from '@/react-query/insights/usePracticeGroups';
import type { TPracticeGroup } from '@/react-query/insights/usePracticeGroups';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import {
  endDateAtom,
  isCountsAtom,
  materialityFilterOpportunityCountsAtom,
  materialityFilterOpportunityDollarsAtom,
  materialityFilterProductionCountsAtom,
  materialityFilterProductionDollarsAtom,
  selectedPracticeGroupsAtom,
  selectedPracticesAtom,
  selectedProviderRoleAtom,
  startDateAtom,
  taskTypeAtom,
  txTypeAtom,
} from '../../atoms';
import { useGetFilteredClinicalData } from '../../helpers/data/getClinicalData';
import {
  useGetAvailableTasks,
  useGetAvailableTreatments,
} from '../../helpers/getAvailableTreatments';
import {
  taskCodesToTypes,
  taskTypesToCodes,
} from '../../helpers/taskTypeHelpers';

import { MaterialityFilter } from './MaterialityFilter';
import { SavedViews } from './SavedViews';

export const Filters = ({
  selectedDrilldownPractices,
  selectedDrilldownProviders,
  enableProviderRoleFilter,
  enableTasksFilter,
  enableMaterialityFilter,
  enablePracticeFilter,
}: {
  selectedDrilldownPractices?: string[];
  selectedDrilldownProviders?: string[];
  enableProviderRoleFilter?: boolean;
  enableTasksFilter?: boolean;
  enableMaterialityFilter?: boolean;
  enablePracticeFilter?: boolean;
}) => {
  const { trackEvent } = useTrackEvent();
  const [txType, setTxType] = useAtom(txTypeAtom);
  const [taskType, setTaskType] = useAtom(taskTypeAtom);
  const [selectedProviderRole, setSelectedProviderRole] = useAtom(
    selectedProviderRoleAtom,
  );

  const [startDate, setStartDate] = useAtom(startDateAtom);
  const [endDate, setEndDate] = useAtom(endDateAtom);
  const [
    materialityFilterProductionDollars,
    setMaterialityFilterProductionDollars,
  ] = useAtom(materialityFilterProductionDollarsAtom);
  const [
    materialityFilterProductionCounts,
    setMaterialityFilterProductionCounts,
  ] = useAtom(materialityFilterProductionCountsAtom);
  const [
    materialityFilterOpportunityDollars,
    setMaterialityFilterOpportunityDollars,
  ] = useAtom(materialityFilterOpportunityDollarsAtom);
  const [
    materialityFilterOpportunityCounts,
    setMaterialityFilterOpportunityCounts,
  ] = useAtom(materialityFilterOpportunityCountsAtom);

  const [selectedPractices, setSelectedPractices] = useAtom(
    selectedPracticesAtom,
  );
  const [selectedRegions, setSelectedRegions] = useAtom(
    selectedPracticeGroupsAtom,
  );
  const [isCounts, setIsCounts] = useAtom(isCountsAtom);

  const availableTreatments = useGetAvailableTreatments(
    selectedDrilldownPractices,
    selectedDrilldownProviders,
  );

  const availableTasks = useGetAvailableTasks(
    selectedDrilldownPractices,
    selectedDrilldownProviders,
  );

  const { data: practiceGroups } = useGetPracticeGroups();

  const { data: insights } = useGetFilteredClinicalData({
    isPracticesEnabled: false,
  });
  const practices = insights?.practices?.map(
    (practice) => practice.practiceName,
  );

  const activePracticeGroups = practiceGroups?.data.filter(
    (group) => group.active,
  );

  const handleSelectPracticeGroups = (items: TPracticeGroup[]) => {
    setSelectedRegions(items.map((item) => item.id));

    // Get all practices from selected regions
    const practicesFromSelectedRegions = items.flatMap((group) =>
      group.practices.map((p: { practiceName: string }) => p.practiceName),
    );

    // Keep practices that are either:
    // 1. In the selected regions, OR
    // 2. Were manually selected and are not in any selected region
    const uniquePractices = practices?.filter((practice) => {
      const isInSelectedRegion =
        practicesFromSelectedRegions.includes(practice);
      const isManuallySelected = selectedPractices?.includes(practice);
      const isInAnySelectedRegion = activePracticeGroups
        ?.filter((group) => selectedRegions.includes(group.id))
        .some((group) =>
          group.practices
            .map((p: { practiceName: string }) => p.practiceName)
            .includes(practice),
        );

      return (
        isInSelectedRegion || (isManuallySelected && !isInAnySelectedRegion)
      );
    });

    setSelectedPractices(uniquePractices ?? []);
    trackEvent(mixpanelEvents.insightsViewFilterChange, {
      filterType: 'region',
      selectedRegions: items.map((item) => item.id),
      selectedPractices: uniquePractices,
    });
  };

  const handleSelectPractices = (items: string[]) => {
    setSelectedPractices(items);
    const practiceGroupsFromSelectedPractices = activePracticeGroups?.filter(
      (group) => {
        const practiceIds = group.practices.map(
          (p: { practiceName: string }) => p.practiceName,
        );
        const selectedPracticeIds = items;
        return practiceIds.every((id) => selectedPracticeIds.includes(id));
      },
    );
    const newSelectedRegions =
      practiceGroupsFromSelectedPractices?.map((group) => group.id) ?? [];
    setSelectedRegions(newSelectedRegions);
    trackEvent(mixpanelEvents.insightsViewFilterChange, {
      filterType: 'practice',
      selectedPractices: items,
      selectedRegions: newSelectedRegions,
    });
  };

  const providerRoleItems: TProviderRole[] = ['HYGIENIST', 'DENTIST'];

  return (
    <div className="vai-flex vai-flex-row vai-justify-between vai-pb-2 vai-w-full">
      <div className="vai-flex vai-flex-row vai-gap-2 vai-flex-wrap vai-w-[calc(100%-175px)]">
        {enablePracticeFilter && (
          <>
            <div className="vai-w-auto">
              <SearchableCheckboxDropdown<TPracticeGroup>
                items={activePracticeGroups || []}
                selectedItems={
                  activePracticeGroups?.filter((group) =>
                    selectedRegions.includes(group.id),
                  ) || []
                }
                onChange={(items) => handleSelectPracticeGroups(items)}
                getItemLabel={(item: TPracticeGroup) => item.name}
                getItemValue={(item: TPracticeGroup) => item.id}
                placeholder="Regions"
                selectionPhrase="Region"
                buttonClassName="vai-w-full"
                includeAllOption={true}
                isUnsorted={true}
              />
            </div>
            <div className="vai-w-auto">
              <SearchableCheckboxDropdown
                getItemLabel={(item) => item}
                getItemValue={(item) => item}
                items={practices || []}
                selectedItems={selectedPractices}
                selectionPhrase="Practice"
                placeholder="Practices"
                onChange={(items) => handleSelectPractices(items)}
                buttonClassName="vai-w-full"
                includeAllOption={true}
                isUnsorted={true}
              />
            </div>
          </>
        )}

        {enableProviderRoleFilter && (
          <div className="vai-w-auto">
            <CheckboxDropdown
              getItemLabel={(item) =>
                `${item?.[0]?.toUpperCase()}${item?.slice(1).toLowerCase()}`
              }
              getItemValue={(item) => item}
              items={providerRoleItems}
              selectedItems={selectedProviderRole}
              placeholder="Provider Role"
              onChange={(items) => {
                setSelectedProviderRole(items);
                trackEvent(mixpanelEvents.insightsViewFilterChange, {
                  filterType: 'providerRole',
                  providerRole: items,
                });
              }}
              includeAllOption={true}
              buttonClassName="vai-w-full"
            />
          </div>
        )}

        {!enableTasksFilter && (
          <div className="vai-w-auto">
            <CheckboxDropdown
              getItemLabel={(item) => item}
              getItemValue={(item) => item}
              items={availableTreatments}
              selectedItems={txType}
              placeholder="Treatments"
              onChange={(items) => {
                setTxType(items);
                trackEvent(mixpanelEvents.insightsViewFilterChange, {
                  filterType: 'txType',
                  txType: items,
                });
              }}
              includeAllOption={true}
              buttonClassName="vai-w-full"
            />
          </div>
        )}

        {enableTasksFilter && (
          <div className="vai-w-auto">
            <CheckboxDropdown
              getItemLabel={(item) => item}
              getItemValue={(item) => item}
              items={availableTasks}
              selectedItems={taskCodesToTypes(taskType)}
              placeholder="Tasks"
              onChange={(items) => {
                setTaskType(taskTypesToCodes(items));
                trackEvent(mixpanelEvents.insightsViewFilterChange, {
                  filterType: 'taskType',
                  taskType: items,
                });
              }}
              includeAllOption={true}
              buttonClassName="vai-w-full"
            />
          </div>
        )}

        <div className="vai-w-88 vai-flex vai-flex-row vai-gap-2 vai-items-center">
          <DatePickerWithRange
            date={{
              from: startDate,
              to: endDate,
            }}
            setDate={(date) => {
              const start = date?.from ?? new Date();
              const end = date?.to ?? new Date();
              setStartDate(start);
              setEndDate(end);
              trackEvent(mixpanelEvents.insightsViewFilterChange, {
                filterType: 'date',
                startDate: start,
                endDate: end,
              });
            }}
          />
          <span className="vai-text-sm vai-text-muted-foreground vai-mr-8">
            vs prev period
          </span>
        </div>

        <SavedViews />
      </div>

      <div className="vai-flex vai-flex-row vai-gap-1 vai-align-top">
        {enableMaterialityFilter && (
          <MaterialityFilter
            productionValue={
              isCounts
                ? materialityFilterProductionCounts
                : materialityFilterProductionDollars
            }
            setProductionValue={
              isCounts
                ? setMaterialityFilterProductionCounts
                : setMaterialityFilterProductionDollars
            }
            opportunityValue={
              isCounts
                ? materialityFilterOpportunityCounts
                : materialityFilterOpportunityDollars
            }
            setOpportunityValue={
              isCounts
                ? setMaterialityFilterOpportunityCounts
                : setMaterialityFilterOpportunityDollars
            }
            max={isCounts ? 10 : 5000}
            isCounts={isCounts}
          />
        )}
        <Button
          className={cn(
            'vai-w-12 vai-h-10',
            !isCounts
              ? 'vai-bg-sapphire-50 vai-text-primary hover:vai-bg-sapphire-100 hover:vai-text-primary'
              : 'vai-bg-white hover:vai-bg-zinc-100',
          )}
          onClick={() => setIsCounts(false)}
          variant="outline"
        >
          <FontAwesomeIcon
            className={cn(!isCounts && 'vai-text-primary')}
            icon={faDollarSign}
          />
        </Button>
        <Button
          className={cn(
            'vai-w-12 vai-h-10',
            isCounts
              ? 'vai-bg-sapphire-50 vai-text-primary hover:vai-bg-sapphire-100 hover:vai-text-primary'
              : 'vai-bg-white hover:vai-bg-zinc-100',
          )}
          onClick={() => setIsCounts(true)}
          variant="outline"
        >
          <FontAwesomeIcon
            className={cn(isCounts && 'vai-text-primary')}
            icon={faHashtag}
          />
        </Button>
      </div>
    </div>
  );
};
